import {
	CellRendererProps,
	ColumnData,
	ColumnRendererComponent,
	ColumnRendererProps,
	SortConfig,
	TData,
} from '@dealstack/frontend/shared/components/Table/Table/types';
import {
	CHECKBOX_COLUMN_WIDTH,
	ColumnsPerLevel,
	UseTable,
} from '@dealstack/frontend/shared/components/Table/Table/useTable';
import { Checkbox } from '@mantine/core';
import { IconArrowNarrowDown, IconArrowNarrowUp } from '@tabler/icons-react';
import classNames from 'classnames';
import { ReactNode, useMemo } from 'react';

interface Props<T extends TData> {
	tableInstance: UseTable<T>;
}

export function DefaultColumnRenderer(props: ColumnRendererProps): JSX.Element {
	const { className, column, sortConfig, style, onSortingClick } = props;

	const currentlySorted = sortConfig?.key === column.id;
	const hasSubColumns = !!props.column.subcolumns.length;

	const getSortIcon = () => {
		if (!column.sortable) {
			return null;
		}
		if (currentlySorted && sortConfig.direction === 'desc') {
			return <IconArrowNarrowUp opacity={0.6} />;
		}
		return <IconArrowNarrowDown opacity={currentlySorted ? 0.6 : 0.2} />;
	};

	return (
		<div className={className} style={hasSubColumns ? {} : style}>
			<div
				className={classNames('flex items-center leading-normal', {
					'font-bold pt-1': !hasSubColumns,
					'cursor-pointer': column.sortable,
				})}
				style={hasSubColumns ? style : {}}
				onClick={() => column.sortable && onSortingClick && onSortingClick(column.id)}
			>
				{getSortIcon()}
				{column.label}
			</div>
		</div>
	);
}

export function DefaultCellRenderer<T extends TData>(props: CellRendererProps<T>): JSX.Element {
	const { className, style, column, item, cellValueFn } = props;

	const value = useMemo(() => {
		const val: unknown = cellValueFn(column, item);

		if (val != null) {
			const type = typeof val;

			if (['boolean', 'string', 'number'].includes(type)) {
				return val as unknown;
			} else {
				// eslint-disable-next-line no-console
				console.warn(
					`[DefaultCellRenderer] Found value in item ${item.id} for column ${column.id} but it has unsupported data type (${type}).`
				);

				return null;
			}
		}

		return null;
	}, [column, item, cellValueFn]);

	return (
		<div className={className} style={style} data-testid={column.dataTestId}>
			{value as ReactNode}
		</div>
	);
}

interface ColumnProps {
	columnId: string;
	columnsPerLevel: ColumnsPerLevel;
	ColumnRenderer: ColumnRendererComponent;
	sortConfig?: SortConfig;
	style?: React.CSSProperties;
	onSortingClick?: (key: string) => void;
}

interface ColumnGridPartProps {
	columns: ColumnData[];
	ColumnRenderer: ColumnRendererComponent;
	sortConfig?: SortConfig;
	onSortingClick?: (key: string) => void;
}

const calculateGridTemplateColumns = (columns: ColumnData[]) => {
	return columns
		.map((subcolumn, index) => (index !== columns.length - 1 ? `${subcolumn.width}px` : '1fr'))
		.join(' ');
};

function ColumnGridPart(props: ColumnGridPartProps) {
	const { columns, sortConfig, onSortingClick, ColumnRenderer } = props;

	if (columns.length === 0) {
		return null;
	}

	const firstColumnOffset = columns[0].offset || 0;

	return (
		<div
			className="grid"
			style={{
				gridTemplateColumns:
					(firstColumnOffset > 0 ? `${firstColumnOffset}px ` : '') +
					calculateGridTemplateColumns(columns),
			}}
		>
			{firstColumnOffset > 0 && <div style={{ gridColumn: '1 / 2' }}></div>}
			{columns.map((subcolumn) => (
				<ColumnRenderer
					key={subcolumn.id}
					column={subcolumn}
					sortConfig={sortConfig}
					style={{
						maxWidth: `${subcolumn.width}px`,
					}}
					onSortingClick={onSortingClick}
				/>
			))}
		</div>
	);
}

function ColumnContainer(props: ColumnProps) {
	const { columnId, columnsPerLevel, sortConfig, onSortingClick, ColumnRenderer } = props;

	return (
		<div className="flex flex-col justify-end">
			{columnsPerLevel[columnId].map((columns, index) => (
				<ColumnGridPart
					key={index}
					columns={columns}
					ColumnRenderer={ColumnRenderer}
					sortConfig={sortConfig}
					onSortingClick={onSortingClick}
				/>
			))}
		</div>
	);
}

export function Table<T extends TData>(props: Props<T>) {
	const { tableInstance } = props;

	return (
		<div
			ref={tableInstance.setScrollContainer}
			className="overflow-auto relative"
			style={{ height: `${tableInstance.tableHeight}px` }}
		>
			<div style={{ height: `${tableInstance.virtualHeight}px`, width: '100%' }}>
				<div
					className="grid"
					style={{
						gridTemplateColumns: tableInstance.hasPinnedColumns
							? `${tableInstance.rowSelect ? tableInstance.pinnedColumnsWidth + CHECKBOX_COLUMN_WIDTH : tableInstance.pinnedColumnsWidth}px auto`
							: `auto`,
					}}
				>
					{tableInstance.hasPinnedColumns && (
						<div
							className="sticky top-0 left-0 z-20 bg-white grid overflow-hidden shadow-[-17px_0_10px_-15px_inset_rgba(0,0,0,0.1)] pb-4"
							style={{
								gridTemplateColumns: `${
									tableInstance.rowSelect ? `${CHECKBOX_COLUMN_WIDTH}px ` : ''
								}${tableInstance.pinnedColumns
									.map((c) => `${c.width}px`)
									.join(' ')}`,
							}}
						>
							{tableInstance.rowSelect && (
								<div className="flex flex-col justify-end items-center p-4">
									<Checkbox
										size="sm"
										onChange={(e) => {
											tableInstance.onSelectAllButton(e.target.checked);
										}}
										checked={tableInstance.selectedAll}
									/>
								</div>
							)}
							{tableInstance.pinnedColumns.map((column) => (
								<ColumnContainer
									key={column.id}
									columnId={column.id}
									columnsPerLevel={tableInstance.columnsPerLevel}
									sortConfig={tableInstance.sortConfig}
									onSortingClick={tableInstance.onSortingClick}
									ColumnRenderer={tableInstance.ColumnRenderer}
								/>
							))}
						</div>
					)}

					<div
						className="sticky top-0 left-0 z-10 grid bg-white pb-4"
						style={{
							gridTemplateColumns: calculateGridTemplateColumns(
								tableInstance.nonPinnedColumns
							),
						}}
					>
						{tableInstance.nonPinnedColumns.map((column) => (
							<ColumnContainer
								key={column.id}
								columnId={column.id}
								columnsPerLevel={tableInstance.columnsPerLevel}
								sortConfig={tableInstance.sortConfig}
								onSortingClick={tableInstance.onSortingClick}
								ColumnRenderer={tableInstance.ColumnRenderer}
							/>
						))}
					</div>

					{tableInstance.hasPinnedColumns && (
						<div
							className="sticky left-0 z-10 bg-white grid grid-cols-2 overflow-hidden shadow-[-17px_0_10px_-15px_inset_rgba(0,0,0,0.1)] "
							style={{
								gridTemplateColumns: `${
									tableInstance.rowSelect ? `${CHECKBOX_COLUMN_WIDTH}px ` : ''
								}${tableInstance.deepestPinnedColumns
									.map((subcolumn) => `${subcolumn.width}px`)
									.join(' ')}`,
								gridAutoRows: `${tableInstance.rowSize}px`,
							}}
						>
							{tableInstance.dataToRender.flatMap((item) => [
								tableInstance.rowSelect && (
									<div
										key={`checkbox-${item.id}`}
										className="flex items-center justify-center border-b border-solid border-gray-300"
										style={{ gridRow: item.gridRow }}
									>
										<Checkbox
											size="sm"
											onChange={() =>
												tableInstance.onSelectedRowToggle(item.id)
											}
											checked={
												tableInstance.isRowSelected(item.id) ||
												tableInstance.selectedAll
											}
										/>
									</div>
								),
								tableInstance.deepestPinnedColumns.map((column) => (
									<div
										key={`${column.id}-${item.id}`}
										className="flex items-center overflow-hidden border-b border-solid border-gray-300"
										style={{ gridRow: item.gridRow }}
									>
										<tableInstance.CellRenderer
											column={column}
											item={item}
											cellValueFn={tableInstance.cellValueFn}
										/>
									</div>
								)),
							])}
						</div>
					)}
					<div
						className="grid overflow-hidden bg-white"
						style={{
							height: `${tableInstance.virtualHeight}px`,
							gridTemplateColumns: calculateGridTemplateColumns(
								tableInstance.deepestNonPinnedColumns
							),
							gridAutoRows: `${tableInstance.rowSize}px`,
						}}
					>
						{tableInstance.dataToRender.flatMap((item) =>
							tableInstance.deepestNonPinnedColumns.map((column) => (
								<div
									key={`${column.id}-${item.id}`}
									className="flex items-center border-b border-solid border-gray-300 overflow-hidden"
									style={{ gridRow: item.gridRow }}
								>
									<tableInstance.CellRenderer
										column={column}
										item={item}
										cellValueFn={tableInstance.cellValueFn}
										style={{
											maxWidth: `${column.width}px`,
										}}
									/>
								</div>
							))
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
